import { useEffect, useState } from "react";
import { Activity, CheckCircle2, CircleAlert, Triangle } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { formatCount } from "@/lib/format";
import type { LogStatsResponse } from "@/lib/types";

type Counts = { total: number; errors: number; warnings: number; loaded: boolean };

const REFRESH_MS = 20_000;
const REFRESH_JITTER_PCT = 0.2;

export function KpiTiles() {
  const [counts, setCounts] = useState<Counts>({ total: 0, errors: 0, warnings: 0, loaded: false });

  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;

    const nextDelayMs = () => {
      const jitter = (Math.random() * 2 - 1) * REFRESH_JITTER_PCT;
      return Math.max(5_000, Math.round(REFRESH_MS * (1 + jitter)));
    };

    const refresh = async () => {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const startParam = `start_time=${encodeURIComponent(start.toISOString())}`;
      try {
        const stats = await api.get<LogStatsResponse>(`/api/logs/stats?${startParam}`);
        if (!cancelled) {
          setCounts({
            total: stats.total,
            errors: stats.errors,
            warnings: stats.warnings,
            loaded: true,
          });
        }
      } catch {
        // ignore — Tiles dürfen im Show-Mode nicht zur Hauptstörung werden
      }
    };

    const tick = async () => {
      await refresh();
      if (!cancelled) {
        timer = window.setTimeout(() => void tick(), nextDelayMs());
      }
    };

    void tick();

    return () => {
      cancelled = true;
      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, []);

  const okCount = Math.max(0, counts.total - counts.errors - counts.warnings);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Tile
        label={t("Events heute")}
        value={formatCount(counts.total)}
        sub={
          counts.loaded
            ? t("{ok} OK · {warn} WARN", { ok: formatCount(okCount), warn: formatCount(counts.warnings) })
            : t("lade…")
        }
        Icon={Activity}
        tone="default"
      />
      <Tile
        label={t("Errors heute")}
        value={formatCount(counts.errors)}
        sub={counts.errors === 0 ? t("alles ruhig") : t("Aufmerksamkeit nötig")}
        Icon={CircleAlert}
        tone={counts.errors > 0 ? "destructive" : "success"}
      />
      <Tile
        label={t("Warnungen")}
        value={formatCount(counts.warnings)}
        sub={t("seit Mitternacht")}
        Icon={Triangle}
        tone={counts.warnings > 0 ? "warning" : "success"}
      />
      <Tile label={t("Status")} value={t("online")} sub={t("Live-Verbindung aktiv")} Icon={CheckCircle2} tone="success" />
    </div>
  );
}

function Tile({
  label,
  value,
  sub,
  tone,
  Icon,
}: {
  label: string;
  value: string;
  sub: string;
  tone: "default" | "destructive" | "warning" | "success";
  Icon: typeof Activity;
}) {
  const toneClass =
    tone === "destructive"
      ? "text-destructive"
      : tone === "warning"
        ? "text-warning"
        : tone === "success"
          ? "text-success"
          : "text-foreground";
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Icon className={"h-3 w-3 " + toneClass} />
        {label}
      </div>
      <div className={`mt-1 text-2xl font-medium tabular-nums ${toneClass}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground">{sub}</div>
    </div>
  );
}
